<script setup>
import DatePicker from "@/components/DatePicker.vue"
import { useCustomerStore } from "@/stores/CustomersStore"
import { useModalStore } from '@/stores/ModalStore'
import { useCalendarStore } from "@/views/apps/calendar/useCalendarStore"
import { nextTick, ref } from "vue"
import { createValidationRules } from "@/utils/validationRules"
import { useI18n } from "vue-i18n"
import { generateUniqueId } from "@/utils/strings"
import { abortRequestsGetThatStartsWith } from "@/utils/backend"
import PhoneInput from "@/components/inputs/PhoneInput.vue"
import PanelTitleDivider from "@/components/PanelTitleDivider.vue"
import TagSelector from "@/components/TagSelector.vue"
import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js'
import debounce from 'lodash/debounce'
import { useDisplay } from "vuetify"

const props = defineProps({

  // isCollapsed: {
  //   type: Number, // VExpansionPanels expects a Number for v-model
  //   default: null,
  // },
  // showAlertAboutExistingClient: {
  //   type: Boolean,
  //   default: false,
  // },
  foundCustomers: {
    type: Array,
    default: () => [],
  },
  customerData: {
    type: Object,
    default: () => ({}),
  },
  isLoadingLocaly: {
    type: Boolean,
    default: false,
  },
})

//selectedCustomerID
const emit = defineEmits([
  'checkIfClientExist', // Check if client exists based on phone number
  'clearFoundCustomers', // Clear found customers when phone number is changed
])

const customerCardEmailFieldRef = ref(null)

const customerCardCustomerNameRef = ref(null)

const { t } = useI18n()
const validationRules = createValidationRules(t)
const customerStore = useCustomerStore()
const modalStore = useModalStore()
const calendarStore = useCalendarStore()
const { mobile } = useDisplay()
const customerDataFormEl = ref() // Reference to the customer card form
const localCustomerData = ref(props.customerData)
// const isCollapsedInitially = ref(null) // the state of the expansion panel
const customerPhoneInputRef = ref(null) // Reference to the phone input field
const moreDataCollapse = ref(null)
const moreDataFieldsRef = ref(null)

const moreDataCompletedFieldsCount = computed(() => {
  const data = localCustomerData.value || {}
  const fields = [
    data.street,
    data.postalCode,
    data.city,
    data.nip,
    data.pesel,
    data.birth,
  ]

  const textFieldsCount = fields.filter(value => String(value || '').trim()).length
  const sexCount = data.sex && data.sex !== '0' ? 1 : 0
  const groupsCount = Array.isArray(data.groups) && data.groups.length > 0 ? 1 : 0

  return textFieldsCount + sexCount + groupsCount
})

const scrollToMoreDataFields = async value => {
  if (value !== 'show') return

  await nextTick()

  setTimeout(() => {
    requestAnimationFrame(() => {
      const element = moreDataFieldsRef.value?.$el || moreDataFieldsRef.value

      element?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    })
  }, 300)

}

// VRadio can't select a null value, so "salon settings" (null) is mapped to a sentinel
const requirePrepaymentModel = computed({
  get: () => localCustomerData.value.requirePrepayment ?? 'salon',
  set: value => {
    localCustomerData.value.requirePrepayment = value === 'salon' ? null : value
  },
})

//Format birth date for date picker
const birthDate = computed({
  get() {
    // Always return a Date object or null, never a string
    if (!localCustomerData.value.birth) {
      return null
    }

    if (typeof localCustomerData.value.birth === 'string') {
      return new Date(localCustomerData.value.birth)
    }

    return localCustomerData.value.birth
  },
  set(newValue) {
    // Ensure birth is always stored as Date object
    localCustomerData.value.birth = newValue
  },
})

// Format customer data method for form submission
const getCustomerFormData = () => {
  const customerData = { ...localCustomerData.value }

  // Format birth date to YYYY-MM-DD
  if (customerData.birth instanceof Date && !isNaN(customerData.birth.getTime())) {
    const year = customerData.birth.getFullYear()
    const month = String(customerData.birth.getMonth() + 1).padStart(2, '0')
    const day = String(customerData.birth.getDate()).padStart(2, '0')
    customerData.birth = `${year}-${month}-${day}`
  } else if (customerData.birth) {
    // Try to parse the date if it's a string or other format
    const parsedDate = new Date(customerData.birth)
    if (!isNaN(parsedDate.getTime())) {
      const year = parsedDate.getFullYear()
      const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
      const day = String(parsedDate.getDate()).padStart(2, '0')
      customerData.birth = `${year}-${month}-${day}`
    } else {
      customerData.birth = null
    }
  } else {
    customerData.birth = null
  }

  return {
    ...customerData,
    clientGroups: Array.isArray(customerData.groups)
      ? customerData.groups.map(group => Number(group.id))
      : [],
    requirePrepayment: customerData.requirePrepayment ?? null,
    tags: customerData.tags ?? [],
  }
}

// Reset form to blank state or selected customer
const resetCustomerForm = () => {
  if (modalStore.modals?.addNewCustomer) {
    // Reset to blank form for new customer
    localCustomerData.value = { ...customerStore.blankCustomer }
  }
  // Reset validation
  if (customerDataFormEl.value) {
    customerDataFormEl.value.resetValidation()
  }
}

// Auto-fill form fields from search input
const autoFillFromSearch = () => {

  if (!modalStore.modals?.addNewCustomer || !customerStore.searchedCustomer || customerStore.selectedCustomer?.salonerClientId) return
  //
  const input = customerStore.searchedCustomer.trim()

  // Detect a phone number
  const phoneRegex = /\+?\d{3,15}/
  const phoneMatch = input.match(phoneRegex)
  const phone = phoneMatch ? phoneMatch[0] : ""

  // Parse name and surname
  const remaining = phone ? input.replace(phone, "").trim() : input
  const parts = remaining.split(/\s+/)

  if (parts.length === 1) {
    localCustomerData.value.name = parts[0]
  } else if (parts.length >= 2) {
    localCustomerData.value.name = parts[0]
    localCustomerData.value.surname = parts.slice(1).join(" ")
  }

  if (phone) {
    localCustomerData.value.phone = phone
  }
}


const handleDuplicatedCustomers = debounce(async() => {
  if (!modalStore.modals?.addNewCustomer) return

  let parsed = null
  let isValidNumber

  try {
    parsed = parsePhoneNumber(localCustomerData.value.phone)
    isValidNumber = isValidPhoneNumber(localCustomerData.value.phone, parsed.country)

    //console.log('isValidNumber:', isValidNumber, 'parsed:', parsed)

    // parsed.number z prefix, parsed.nationalNumber bez prefix
    // 1. Czy numer jest valid, w każdym przypadku
    // 2. W przypadku PL sprawdzam czy ma 9 cyfr
    // 3. W przypadku nie PL, nie sprawdzam czy ma 9 cyfr

    if (isValidNumber && (
      (parsed.country === 'PL' && parsed.nationalNumber?.length === 9) ||
      parsed.country !== 'PL'
    ))
    {
      // emit number WITH prefix
      emit('checkIfClientExist', parsed.number)
    } else {
      //emit('clearFoundCustomers')
      //abortRequestsGetThatStartsWith('/clients/checkClientExists')
    }

  } catch (error) {}

}, 250)

const selectExistingCustomer = async (selectedCustomer) => {

  if (selectedCustomer) {
    await customerStore.setSelectedCustomer(selectedCustomer)
    await customerStore.getCustomerVouchersAndTickets(selectedCustomer.salonerClientId)

    showToast(t('customerSelected'), 'success')
    // Close the modal only if not opening the customer card
    modalStore.closeModal('addNewCustomer')

  }
}
// 1 woman, 2 man
// 0 yes / 1 no

watch(() => props.customerData, (newData) => {
  if (newData) {
    // Initialize local customer data with the new data
    localCustomerData.value = { ...newData }

    // Normalize to number so it matches the select items (BE may return "0"/"1")
    if (newData.requirePrepayment != null) {
      localCustomerData.value.requirePrepayment = Number(newData.requirePrepayment)
    }
  }
}, { immediate: true })

onMounted( () => {
  // Adding a new customer case
  if (modalStore.modals?.addNewCustomer) {
    resetCustomerForm() // first reset to blank

    localCustomerData.value.notificationSms = "1" //set default
    // localCustomerData.value.marketingEmail = "0" //set default

    autoFillFromSearch() // then auto-fill

    customerCardCustomerNameRef.value.focus()
  }
})


// Exposing data
defineExpose({
  customerDataFormEl,
  getCustomerFormData,
  resetCustomerForm,
  customerPhoneInputRef,
  getPhoneValue: () => localCustomerData.value.phone,
})
</script>

<template>
  <div class="position-relative">
    <VForm
      ref="customerDataFormEl"
      class="pt-2"
      :class="{ 'opacity-80': customerStore.isLoading }"
    >
      <VRow>
        <VCol cols="12">
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                ref="customerCardCustomerNameRef"
                v-model="localCustomerData.name"
                :disabled="customerStore.isLoading"
                density="compact"
                :label="$t('name')"
                color="secondary"
                variant="outlined"
                flat
                type="text"
                :autocomplete="generateUniqueId()"
              />
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VTextField
                v-model="localCustomerData.surname"
                :disabled="customerStore.isLoading"
                density="compact"
                :label="$t('lastname')"
                color="secondary"
                variant="outlined"
                flat
                type="text"
                :autocomplete="generateUniqueId()"
              />
            </VCol>
            <VCol
              cols="12"
              md="12"
              lg="6"
            >
              <PhoneInput
                ref="customerPhoneInputRef"
                v-model="localCustomerData.phone"
                :disabled="customerStore.isLoading"
                :is-loading="props.isLoadingLocaly"
                @update:model-value="() => {
                  abortRequestsGetThatStartsWith('/clients/checkClientExists');
                  emit('clearFoundCustomers')
                  handleDuplicatedCustomers()
                }"
              />
              <!--              <VTextField-->
              <!--                ref="customerPhoneInputRef"-->
              <!--                v-model="localCustomerData.phone"-->
              <!--                density="compact"-->
              <!--                :label="$t('phone')"-->
              <!--                color="secondary"-->
              <!--                variant="outlined"-->
              <!--                flat-->
              <!--                type="number"-->
              <!--                hide-spin-buttons-->
              <!--                :rules="[validationRules.phoneRules[0]]"-->
              <!--                :autocomplete="generateUniqueId()"-->
              <!--                @input="localCustomerData.phone = localCustomerData.phone ? localCustomerData.phone.toString().slice(0, 12) : ''"-->
              <!--                @update:model-value="handleDuplicatedCustomers"-->
              <!--              >-->
              <!--                <template #append-inner>-->
              <!--                  <VProgressCircular-->
              <!--                    v-if="props.isLoadingLocaly"-->
              <!--                    size="x-small"-->
              <!--                    indeterminate-->
              <!--                  />-->
              <!--                </template>-->
              <!--              </VTextField>-->
            <!-- @input ? -->
            </VCol>
            <VCol
              cols="12"
              md="12"
              lg="6"
            >
              <PhoneInput
                ref="customerPhoneInputRef"
                v-model="localCustomerData.additionalPhone"
                :disabled="customerStore.isLoading"
                :phone-number-label="$t('additionalPhone')"
              />
            </VCol>
            <VCol cols="12">
              <VTextField
                ref="customerCardEmailFieldRef"
                v-model="localCustomerData.email"
                :disabled="customerStore.isLoading"
                density="compact"
                label="Email"
                color="secondary"
                variant="outlined"
                flat
                type="text"
                :rules="[validationRules.emailRules[0]]"
                validate-on="blur"
                :autocomplete="generateUniqueId()"
                @update:model-value="customerCardEmailFieldRef?.resetValidation()"
              />
            </VCol>
          </VRow>
          <VRow v-if="foundCustomers.length > 0">
            <VCol cols="12">
              <VAlert
                border="top"
                variant="outlined"
                color="primary"
              >
                <div class="mb-2">
                  <strong>
                    {{ $t('clientExistsInfo') }}
                  </strong>
                  <p>
                    {{ $t('clientExistsInfo2') }}
                  </p>
                </div>

                <div class="customer-list">
                  <VList density="compact">
                    <VListItem
                      v-for="customer in foundCustomers"
                      :key="customer.id || customer.salonerClientId"
                      class="px-0"
                    >
                      <div class="d-flex justify-space-between flex-wrap ga-1">
                        <div class="d-flex flex-wrap ga-1">
                          <span>
                            {{ customer.name }} {{ customer.surname }} {{ customer.phone || '' }} {{ customer.email || '' }}
                          </span>

                          <VChip
                            v-if="customer?.blacklisted"
                            size="small"
                          >
                            <small class="mr-1">{{ $t('blacklisted') }}</small>
                            <VIcon
                              color="error"
                              size="18"
                              icon="ri-alarm-warning-fill"
                            />
                          </VChip>
                        </div>

                        <VBtn
                          size="x-small"
                          variant="flat"
                          color="secondary"
                          @click="selectExistingCustomer(customer)"
                        >
                          <template #prepend>
                            <VIcon
                              size="16"
                              icon="ri-user-follow-line"
                            />
                          </template>
                          {{ $t('select') }}
                        </VBtn>
                      </div>

                      <VDivider class="mt-4" />
                    </VListItem>
                  </VList>
                </div>
              </VAlert>
            </VCol>
          </VRow>
          <VRow>
            <VCol cols="12">
              <VTextarea
                v-model="localCustomerData.notes"
                :disabled="customerStore.isLoading"
                density="compact"
                :label="$t('notes')"
                color="secondary"
                rows="5"
                variant="outlined"
                flat
                type="text"
                :autocomplete="generateUniqueId()"
              />
              <!-- Tags only for existing clients (customer card), not when adding a new one -->
              <TagSelector
                v-model="localCustomerData.tags"
                class="mt-3"
                type="clients"
                dont-hide-close-btn-on-tags
                :disabled="customerStore.isLoading"
              />
            </VCol>
          </VRow>
          <VRow
            dense
            class="mt-4"
          >
            <VCol
              cols="12"
              md="6"
            >
              {{ $t('visitReminders') }}:
              <div>
                <VCheckbox
                  v-model="localCustomerData.notificationSms"
                  :disabled="customerStore.isLoading"
                  label="SMS"
                  true-value="1"
                  false-value="0"
                  density="compact"
                  hide-details
                />
              </div>

              <div>
                <VCheckbox
                  v-model="localCustomerData.notificationEmail"
                  :disabled="customerStore.isLoading"
                  label="E-mail"
                  true-value="1"
                  false-value="0"
                  density="compact"
                  hide-details
                />
              </div>
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              {{ $t('marketing_agree_plural') }}:
              <div>
                <VCheckbox
                  v-model="localCustomerData.marketingSms"
                  :disabled="customerStore.isLoading"
                  label="SMS"
                  true-value="1"
                  false-value="0"
                  density="compact"
                  hide-details
                />
              </div>

              <div>
                <VCheckbox
                  v-model="localCustomerData.marketingEmail"
                  :disabled="customerStore.isLoading"
                  label="E-mail"
                  true-value="1"
                  false-value="0"
                  density="compact"
                  hide-details
                />
              </div>
            </VCol>
            <VCol
              v-if="!modalStore.modals?.addNewCustomer"
              cols="12"
              md="6"
            >
              {{ $t('customerStatus') }}:
              <div>
                <VCheckbox
                  v-model="localCustomerData.blacklisted"
                  :disabled="customerStore.isLoading"
                  :label="$t('customerOnBlackList')"
                  :loading="customerStore.isLoading"
                  true-value="1"
                  false-value="0"
                  density="compact"
                />
              </div>
            </VCol>
<!--            v-if="!modalStore.modals?.addNewCustomer" -->
            <VCol
              v-if="calendarStore.viewSettings.portalPrepaymentEnabled"
              cols="12"
              md="6"
            >
              {{ $t('requirePrepayment') }}:
              <span class="cursor-pointer">
                <VIcon
                  icon="ri-information-line"
                  size="18"
                  color="info"
                />
                <VTooltip
                  activator="parent"
                  max-width="360px"
                  scroll-strategy="close"
                  :open-on-hover="!mobile"
                  :open-on-click="mobile"
                >
                  <span class="text-pre-line">{{ $t('requirePrepaymentInfo') }}</span>
                </VTooltip>
              </span>
              <VRadioGroup
                v-model="requirePrepaymentModel"
                :disabled="customerStore.isLoading"
                inline
                density="compact"
                hide-details
              >
                <VRadio
                  :label="$t('salonSettings')"
                  value="salon"
                />
                <VRadio
                  :label="$t('no')"
                  :value="0"
                />
                <VRadio
                  :label="$t('yes')"
                  :value="1"
                />
              </VRadioGroup>
            </VCol>
          </VRow>
          <VRow>
            <VCol
              class="customer-data-more pt-0"
              cols="12"
              :md="modalStore.modals?.addNewCustomer ? '12' : '12'"
            >
              <!--v-model="isCollapsedInitially"-->
              <VExpansionPanels
                v-model="moreDataCollapse"
                flat
                class="custom-collapse selected-products-collapse px-0"
                @update:model-value="scrollToMoreDataFields"
              >
                <VExpansionPanel
                  value="show"
                  class="px-0"
                >
                  <PanelTitleDivider
                    :text="$t('more_date')"
                    :allow-badge="moreDataCompletedFieldsCount > 0"
                    :badge-content="moreDataCompletedFieldsCount"
                    class="pa-0"
                  />
                  <VExpansionPanelText class="px-0 mt-4">
                    <VRow>
                      <VCol
                        cols="12"
                        md="8"
                      >
                        <VTextField
                          v-model="localCustomerData.street"
                          :disabled="customerStore.isLoading"
                          density="compact"
                          :label="$t('street')"
                          color="secondary"
                          variant="outlined"
                          flat
                          type="text"
                          :autocomplete="generateUniqueId()"
                        />
                      </VCol>
                      <VCol
                        cols="12"
                        md="4"
                      >
                        <VTextField
                          v-model="localCustomerData.postalCode"
                          :disabled="customerStore.isLoading"
                          density="compact"
                          :label="$t('zipcode')"
                          color="secondary"
                          variant="outlined"
                          placeholder="00-000"
                          flat
                          type="text"
                          :rules="[validationRules.zipCodeRules[0]]"
                          validate-on="blur"
                          :autocomplete="generateUniqueId()"
                          @update:model-value="customerCardEmailFieldRef?.resetValidation()"
                        />
                      </VCol>
                      <VCol
                        cols="12"
                        md="8"
                      >
                        <VTextField
                          v-model="localCustomerData.city"
                          :disabled="customerStore.isLoading"
                          density="compact"
                          :label="$t('city')"
                          color="secondary"
                          variant="outlined"
                          flat
                          type="city"
                          :autocomplete="generateUniqueId()"
                        />
                      </VCol>
                      <VCol
                        ref="moreDataFieldsRef"
                        cols="12"
                        md="4"
                      >
                        <VTextField
                          v-model="localCustomerData.nip"
                          :disabled="customerStore.isLoading"
                          density="compact"
                          label="Nip"
                          color="secondary"
                          variant="outlined"
                          flat
                          maxlength="15"
                          :autocomplete="generateUniqueId()"
                        />
                      </VCol>
                      <VCol
                        cols="12"
                        md="4"
                      >
                        <VTextField
                          v-model="localCustomerData.pesel"
                          :disabled="customerStore.isLoading"
                          density="compact"
                          label="Pesel"
                          color="secondary"
                          variant="outlined"
                          flat
                          type="text"
                          maxlength="11"
                          :autocomplete="generateUniqueId()"
                        />
                      </VCol>
                      <VCol
                        cols="12"
                        md="4"
                      >
                        <DatePicker
                          v-model="birthDate"
                          :disabled="customerStore.isLoading"
                          class="mb-md-0 mb-4"
                          :inline="false"
                          :set-icon="false"
                          clearable
                          teleport
                          :label-text="$t('birthDate')"
                        />
                      </VCol>
                      <VCol
                        cols="12"
                        md="4"
                      >
                        <VSelect
                          v-model="localCustomerData.sex"
                          :disabled="customerStore.isLoading"
                          density="compact"
                          :items="[
                            { title: 'Nie podano', value: '0' },
                            { title: 'Kobieta', value: '1' },
                            { title: 'Mężczyzna', value: '2' }
                          ]"
                          :label="$t('sex')"
                          color="secondary"
                          variant="outlined"
                          flat
                          :autocomplete="generateUniqueId()"
                        />
                      </VCol>
                      <VCol cols="12">
                        <VSelect
                          v-model="localCustomerData.groups"
                          :disabled="customerStore.isLoading"
                          density="compact"
                          :label="$t('groups')"
                          color="secondary"
                          :items="calendarStore.clientGroups"
                          item-title="name"
                          item-value="id"
                          variant="outlined"
                          multiple
                          chips
                          closable-chips
                          return-object
                          :autocomplete="generateUniqueId()"
                        />
                      </VCol>
                    </VRow>
                  </VExpansionPanelText>
                </VExpansionPanel>
              </VExpansionPanels>
            </VCol>
          </VRow>
        </VCol>
      </VRow>
    </VForm>
  </div>
</template>

<style lang="scss">
  .centered {
    inset-block-start: 50%;
    inset-inline-start: 50%;
    transform: translate(-50%, -50%);
  }

  .customer-data-more {
    .v-expansion-panel-text__wrapper {
      padding-inline: 0 !important;
    }
  }
</style>
